import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { ButtonSubmit } from "@/components/buttons/ButtonSubmit.tsx";
import { postFlowTutorFileUpload, postFlowTutorMessage } from "@/api/admin.ts";
import { toastAndError } from "@/helpers/toast.ts";
import { postTutorTyping } from "@/api/tutors.ts";
import { useDebouncedCallback, useThrottledCallback } from "@tanstack/react-pacer";
import { type Chat, useChatStore } from "@/stores/useChatStore.ts";
import { Row } from "@/components/common/row.ts";
import { ButtonIcon } from "@/components/buttons/ButtonIcon.tsx";
import { ButtonEmpty } from "@/components/buttons/ButtonEmpty.tsx";
import { type DispatchGPTTextLoading, SignalOn } from "@/types/signalr-functions.ts";
import { IconStar } from "@/icons/IconStar.tsx";
import type { EmojiClickData } from "emoji-picker-react";
import EmojiPicker, { Theme as EmojiPickerTheme } from "emoji-picker-react";
import { IconSmile } from "@/icons/IconSmile.tsx";
import { IconAttachment } from "@/icons/IconAttachment.tsx";
import { IconX } from "@/icons/IconX.tsx";
import { hasSingleDollarLatex } from "@/helpers/latex.tsx";
import { css } from "@linaria/core";
import { IconDollar } from "@/icons/IconDollar.tsx";
import { IconRightLine } from "@/icons/IconRightLine.tsx";
import { IconTick } from "@/icons/IconTick.tsx";
import type { AssignedSession } from "@/stores/useAssignedChatStore.ts";
import type { SimpleError } from "@/api/shared.ts";
import { useInputStore } from "@/stores/useInputStore.ts";
import { LatexKeyboard } from "@/components/LatexKeyboard.tsx";
import { IconKeyboard } from "@/icons/IconKeyboard.tsx";
import { ADT } from "pb.adt";
import { JsonParse } from "@/helpers/json.ts";
import type { NullOrCallback } from "@/helpers/types.ts";
import { useSignalROn } from "@/helpers/signalr/signalrEedi.ts";

type ChatInputProps = {
  chat: Chat;
  assignedSession?: AssignedSession;
  sessionClaimed?: boolean;
  canAwardCoins?: boolean;
  toggleAwardCoins: () => void;
  onInputChange?: (message: string) => void;
};

const ChatInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  flex-grow: 0;
  padding: 0.75rem ${t.chatPaddingSides};

  &[data-latex-keyboard-active="true"] {
    padding-bottom: 270px;
  }
`;

export const SharedChatStyle = css`
  background-color: ${t.chatBoxColor};
  width: 100%;
  min-width: 300px;
  min-height: 100px;
  max-height: 300px;
  border: ${t.brandBorderSegment};
  border-radius: ${t.brandBorderRadius};
  field-sizing: content;
  resize: none;
  transition: all 150ms linear(0, 0.06, 0.25, 0.56, 1 36%, 0.88, 0.81, 0.88, 1 73%, 0.95, 1);
  overflow-y: auto;
  display: block;

  /* Extra padding at the bottom to account for the submit button */
  padding: 0.5rem 0.5rem 2.5rem 0.5rem;

  &[data-is-submitting="true"],
  &:disabled {
    background-color: rgb(197 197 197 / 0.54);
  }
`;

const ChatBox = styled.textarea``;

const BoxWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SubmitWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
`;

const EmojiPickerWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.5rem);
  z-index: 2;
  overflow: hidden;
  border: 1px solid #cbd5e1;
  border-radius: ${t.buttonBorderRadius};
  box-shadow: 0 0.75rem 2rem rgb(15 23 42 / 0.18);
`;

const FileInput = styled.input`
  display: none;
`;

const FileDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: ${t.buttonBorderRadius};
  background: #fff;
`;

const FileName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--14px);
  color: ${t.brandTextPrimary};
`;

const Separator = styled.div`
  width: 1px;
  height: 20px;
  background-color: #cbd5e1;
  margin: 0 1rem 0 0.5rem;
`;

const ChatBoxWrapper = styled.div`
  position: relative;
  display: flex;
`;

const LlmPreview = styled.textarea`
  background-color: ${t.chatBoxColor};
  min-width: 300px;
`;

const Relative = styled.div`
  position: relative;
`;

const IconRightWrapper = styled.div`
  flex-shrink: 0;
  width: 28px;
`;

export const ChatInput: FC<ChatInputProps> = ({
  chat,
  sessionClaimed,
  canAwardCoins = false,
  toggleAwardCoins,
  onInputChange,
}) => {
  const { SetLlmMessage } = useChatStore();
  const { SetFile, SetInput, inputMessage, file } = useInputStore();

  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatBoxRef = useRef<HTMLTextAreaElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [latestLLMPreview, setLatestLLMPreview] = useState<string | null>(null);
  const [lastSentMessageTime, setLastSentMessageTime] = useState<number>(0);
  const [llmActivelyWriting, setLlmActivelyWriting] = useState(false);
  const [hasLatexKeyboard, setHasLatexKeyboard] = useState(false);
  const [latexKeyboardCallback, setlatexKeyboardCallback] = useState<NullOrCallback>(null);
  const [isLatexKeyboardActive, setIsLatexKeyboardActive] = useState(false);

  const message = inputMessage.get(chat.flowGeneratorSessionId);
  const selectedFile = file.get(chat.flowGeneratorSessionId);

  const typingThrottle = useThrottledCallback(
    () => {
      if (chat && !isSubmitting && lastSentMessageTime + 4000 < performance.now())
        postTutorTyping(chat.flowGeneratorSessionId).catch((e) => {
          toastAndError("Unable to send typing update", e);
        });
    },
    {
      leading: true,
      wait: 3500,
    },
  );

  const llmActivelyWritingDebounce = useDebouncedCallback(
    () => {
      setLlmActivelyWriting(false);
    },
    {
      leading: false,
      wait: 1000,
    },
  );

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.focus({});
    }
  }, [chatBoxRef]);

  const handleGPTTextLoading = useCallback((status: string) => {
    try {
      const state = JsonParse<DispatchGPTTextLoading>(status);

      if (state && (state.socratesSessionId !== null || state.socratesSessionId !== "")) {
        if (!state.isLoading) setLlmActivelyWriting(false);
      }
    } catch (e) {
      console.warn("TextLoading got given an invalid string that could not get parsed", status, e);
    }
  }, []);

  useSignalROn(SignalOn.DispatchGPTTextLoading + chat.flowGeneratorSessionId, handleGPTTextLoading);

  useEffect(() => {
    if (chat.llmMessage && chat.llmMessage !== "") {
      if (message?.trim() === "" || chat.llmMessage.includes(message ?? "") || llmActivelyWriting) {
        SetInput(chat.flowGeneratorSessionId, chat.llmMessage.split("{{")[0]);
        llmActivelyWritingDebounce();
        // oxlint-disable-next-line react/set-state-in-effect
      } else if (latestLLMPreview !== chat.llmMessage) setLatestLLMPreview(chat.llmMessage);

      SetLlmMessage(chat?.flowGeneratorSessionId ?? 0, undefined);
    }
  }, [
    chat.llmMessage,
    latestLLMPreview,
    message,
    llmActivelyWritingDebounce,
    llmActivelyWriting,
    SetLlmMessage,
    SetInput,
    chat.flowGeneratorSessionId,
  ]);

  useEffect(() => {
    if (message && message.length > 0) {
      typingThrottle();
    }
  }, [message, typingThrottle]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (emojiPickerRef.current?.contains(target)) return;
      if (emojiButtonRef.current?.contains(target)) return;

      setIsEmojiPickerOpen(false);
    };

    if (isEmojiPickerOpen) {
      document.addEventListener("mousedown", onMouseDown);
    }

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [isEmojiPickerOpen]);

  const submitMessage = () => {
    if (!chat || isSubmitting || (!selectedFile && !message)) return;
    if (!selectedFile && message && message.trim().length === 0) return;

    setIsSubmitting(true);

    // eslint-disable-next-line react-hooks/purity
    setLastSentMessageTime(performance.now());

    if (onInputChange) onInputChange("");

    const request = selectedFile
      ? postFlowTutorFileUpload(chat.flowGeneratorSessionId, selectedFile, message?.trim() ?? "")
      : postFlowTutorMessage(chat.flowGeneratorSessionId, message ?? "");

    return request
      .then(() => {
        SetInput(chat.flowGeneratorSessionId, "");
        SetFile(chat.flowGeneratorSessionId, null);

        SetLlmMessage(chat.flowGeneratorSessionId, "");
        setLatestLLMPreview(null);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      })
      .catch((e) => {
        const err = JsonParse<SimpleError>(e.message);
        if (!err) {
          toastAndError("Unable to parse response sendMessage json");
          return;
        }

        switch (err.statusCode) {
          case 400:
            toastAndError("Unable to send message: " + err.message, e);
            break;

          default:
            toastAndError("Unable to submit message", e);
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const isSomethingLoading = isSubmitting;

  const canSubmit = (message && message.trim().length > 0) || !!selectedFile;
  const onEmojiClick = (emojiData: EmojiClickData) => {
    SetInput(chat.flowGeneratorSessionId, `${message}${emojiData.emoji}`);
    setIsEmojiPickerOpen(false);
  };
  const clearSelectedFile = () => {
    SetFile(chat.flowGeneratorSessionId, null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const repairLatex = () => {
    const repair = (current: string) => {
      const parts = current.split(/(\$\$[\s\S]*?\$\$)/g);

      return parts
        .map((part) => {
          if (part.startsWith("$$") && part.endsWith("$$")) {
            return part;
          }

          return part.replace(
            /(?<!\$)\$([\s\S]*?)(?<!\$)\$(?!\$)/g,
            (_match, content: string) => `$$${content}$$`,
          );
        })
        .join("");
    };
    SetInput(chat.flowGeneratorSessionId, repair(message ?? ""));
  };

  const canRepairLatex = hasSingleDollarLatex(message ?? "");

  const openKeyboardCallback = useCallback((callback: NullOrCallback) => {
    const callbackWrapper = () => {
      setIsLatexKeyboardActive(true);
      if (callback) callback();
    };
    setlatexKeyboardCallback(() => callbackWrapper);
    setHasLatexKeyboard(callback !== null);
  }, []);

  if (!sessionClaimed) {
    return null;
  }

  return (
    <>
      <LatexKeyboard
        onNewInput={(text) => {
          SetInput(chat.flowGeneratorSessionId, (message ?? "") + "$$ " + text + " $$ ");
        }}
        openKeyboardFunc={openKeyboardCallback}
        onFocus={(focused) => setIsLatexKeyboardActive(focused)}
      />
      <ChatInputWrapper data-latex-keyboard-active={isLatexKeyboardActive}>
        <BoxWrapper>
          <ChatBoxWrapper>
            {(latestLLMPreview || ADT.match(chat.socrates.state, ["Idle", "Fetching"])) && (
              <Row data-align={"center"} data-justify={"between"}>
                <Relative>
                  <LlmPreview
                    className={SharedChatStyle}
                    name={"Llm Preview"}
                    value={
                      latestLLMPreview
                        ? latestLLMPreview
                        : ADT.switch(chat.socrates.state, {
                            Idle: () => "Starting to fetch the Socrates Session",
                            Fetching: ({ attempts }) =>
                              `Fetching Socrates Session, this may take up to 20s (Attempt #${attempts})!`,
                            Ready: () => (llmActivelyWriting ? "LLM is thinking... 🤔" : undefined),
                            Failed: ({ reason }) => `Failed to fetch Socrates Session: ${reason}`,
                          })
                    }
                    onChange={(e) => {
                      setLatestLLMPreview(e.target.value);
                    }}
                  />
                  <SubmitWrapper>
                    {latestLLMPreview && (
                      <>
                        <ButtonIcon
                          icon={<IconX color={t.brandNeutralWhite} width={18} />}
                          text={"Reject"}
                          backgroundColor={t.brandAccentRed}
                          onClick={() => {
                            setLatestLLMPreview(null);
                            SetLlmMessage(chat.flowGeneratorSessionId, "");
                          }}
                          flare
                        />
                        <ButtonIcon
                          icon={<IconTick />}
                          text={"Replace"}
                          backgroundColor={t.brandAccentGreen}
                          onClick={() => {
                            SetInput(chat.flowGeneratorSessionId, latestLLMPreview);
                            setLatestLLMPreview(null);
                            SetLlmMessage(chat.flowGeneratorSessionId, "");
                          }}
                          flare
                        />
                      </>
                    )}
                  </SubmitWrapper>
                </Relative>
                <IconRightWrapper>
                  <IconRightLine width={28} height={28} />
                </IconRightWrapper>
                <br />
              </Row>
            )}
            <ChatBox
              ref={chatBoxRef}
              className={SharedChatStyle}
              name="Message Box"
              value={inputMessage.get(chat.flowGeneratorSessionId)}
              onChange={(e) => {
                SetInput(chat.flowGeneratorSessionId, e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submitMessage();
                }
              }}
              disabled={!sessionClaimed}
            />
          </ChatBoxWrapper>

          <FileInput
            ref={fileInputRef}
            type="file"
            disabled={isSomethingLoading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;

              SetFile(chat.flowGeneratorSessionId, file);
            }}
          />
          <SubmitWrapper>
            {canRepairLatex && (
              <ButtonEmpty
                onClick={() => {
                  if (isSomethingLoading || !sessionClaimed) {
                    return;
                  }

                  repairLatex();
                }}
                flare
                tooltip={"Replace all $..$ with $$..$$"}
              >
                <IconDollar />
              </ButtonEmpty>
            )}
            {hasLatexKeyboard && latexKeyboardCallback && (
              <ButtonEmpty onClick={latexKeyboardCallback} flare tooltip={"Open LaTeX keyboard"}>
                <IconKeyboard />
              </ButtonEmpty>
            )}
            {canAwardCoins && (
              <ButtonEmpty
                onClick={() => {
                  if (isSomethingLoading || !sessionClaimed) {
                    return;
                  }
                  toggleAwardCoins();
                }}
                flare
                tooltip={"Award coins"}
              >
                <IconStar width={"22"} height={"22"} />
              </ButtonEmpty>
            )}
            <div ref={emojiButtonRef}>
              <ButtonEmpty
                onClick={() => {
                  if (isSomethingLoading || !sessionClaimed) return;
                  setIsEmojiPickerOpen((open) => !open);
                }}
                flare
                tooltip={"Add an emoji"}
              >
                <IconSmile
                  width={"20"}
                  height={"20"}
                  color={isEmojiPickerOpen ? t.brandPastelBlue : undefined}
                />
              </ButtonEmpty>
            </div>
            <ButtonEmpty
              onClick={() => {
                if (isSomethingLoading || !sessionClaimed) return;
                fileInputRef.current?.click();
              }}
              flare
              tooltip={"Attach a file"}
            >
              <IconAttachment width={"20"} height={"20"} />
            </ButtonEmpty>
            <Separator />
            <ButtonSubmit
              onClick={(callback) => {
                submitMessage()?.finally(() => {
                  callback();
                });
              }}
              disabled={isSubmitting || !canSubmit || !sessionClaimed}
              flare
              tooltip={
                !sessionClaimed
                  ? "You must claim a session to submit messages"
                  : isSubmitting
                    ? "Submitting..."
                    : undefined
              }
            />
            {isEmojiPickerOpen && (
              <EmojiPickerWrapper ref={emojiPickerRef}>
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  autoFocusSearch={false}
                  skinTonesDisabled
                  lazyLoadEmojis={true}
                  theme={EmojiPickerTheme.LIGHT}
                  previewConfig={{ showPreview: false }}
                />
              </EmojiPickerWrapper>
            )}
          </SubmitWrapper>
        </BoxWrapper>
        {selectedFile && (
          <FileDetails>
            <FileName>{selectedFile.name}</FileName>
            {selectedFile.webkitRelativePath}
            <ButtonEmpty
              onClick={(callback) => {
                clearSelectedFile();
                callback();
              }}
            >
              <IconX width={"16"} height={"16"} color={"#64748B"} />
            </ButtonEmpty>
          </FileDetails>
        )}
      </ChatInputWrapper>
    </>
  );
};
